import React from 'react';
import { Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAdmin } from '../contexts/AdminContext';
import type { AdminRole } from '../services/adminService';

/**
 * Gates a route (or any subtree) to one or more admin roles.
 *
 * There was no role-based route guard anywhere in this app before — every
 * signed-in admin could reach every page. This is the one place that
 * changes, so a super_admin-only page can't be reached by URL by a
 * city_admin just because they're logged in.
 */
export const RequireRole: React.FC<{ roles: AdminRole[]; children: React.ReactNode }> = ({
  roles,
  children,
}) => {
  const { loading, adminRecord } = useAdmin();

  if (loading) {
    // Still resolving the admin record — the surrounding Layout's own auth
    // gate handles the "not logged in" case, so a brief spinner here just
    // covers the gap while adminRecord loads.
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!adminRecord || !roles.includes(adminRecord.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

export default RequireRole;
